import axios from "axios";
import { axiosWithRetry } from "./axiosRetry";
import { guardRows } from "./mutationGuard";

// Only background callers opt into sharing. Mutation/manual refreshes start a
// newer generation so an older response can never overwrite their fresh data.
export const createDeliveryListFetch = (api, set, get, onError) => {
  let generation = 0;
  let inFlight = null;
  const page = (number) => axiosWithRetry(
    () => axios.get(`${api}/deliveries?page=${number}&page_size=100`),
    { retries: 2, delay: 500, maxDelay: 1000, silent: true },
  );
  return ({ silent = false, coalesce = false } = {}) => {
    if (coalesce && inFlight) return inFlight;
    const current = ++generation;
    const run = (async () => {
      try {
        const response = await page(1);
        const data = response.data;
        const first = data.items || data;
        if (!Array.isArray(first)) throw new Error("Invalid delivery list");
        const pages = data.total_pages || 1;
        // Show the first page on first load, but never shrink an existing list
        // while later pages are loading or if a later page fails.
        if (current === generation && get().deliveries.length === 0) {
          set({ deliveries: guardRows(first, get().deliveries), loading: false });
        }
        let all = [...first];
        for (let start = 2; start <= pages; start += 3) {
          if (current !== generation) return;
          const batch = await Promise.all(
            Array.from({ length: Math.min(3, pages - start + 1) }, (_, i) => page(start + i)),
          );
          for (const result of batch) {
            const rows = result.data.items || result.data;
            if (!Array.isArray(rows)) throw new Error("Invalid delivery page");
            all = all.concat(rows);
          }
        }
        if (current === generation) {
          set({ deliveries: guardRows(all, get().deliveries), loading: false });
        }
      } catch {
        if (current === generation) {
          if (!silent) onError();
          set({ loading: false });
        }
      }
    })();
    inFlight = run;
    run.finally(() => { if (inFlight === run) inFlight = null; });
    return run;
  };
};